<template>
    <div class="mb-5">
        <h1 class="page-title">{{ title }}</h1>
    </div>

    <div v-if="type !== 'travelBloc'" class="flex justify-end mb-4">
        <Button
            :label="type && type === 'regionBloc' ? 'Ajouter une région' : 'Ajouter une référence'"
            @click="() => type && type === 'regionBloc'
              ? router.push({ name: 'addMiddleMenu', params: { type: 'region' } })
              : router.push({ name: 'addMiddleMenu' })"
            icon="pi pi-plus"
            aria-label="Ajouter une référence"
            class="button-add"
        />
    </div>
    <div class="card">
        <DataTable
            lazy
            :value="tableData"
            :paginator="true"
            :rows="rows"
            removableSort
            :loading="isLoading"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :rowsPerPageOptions="[10, 25, 100]"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            @page="onPaginate"
            @sort="onSort"
        >
            <Column field="title" header="Titre">
                <template #body="{ data }">
                    <span>{{ data.title }}</span>
                </template>
            </Column>

            <Column field="position" header="Position">
                <template #body="{ data }">
                    <span>{{ data.position }}</span>
                </template>
            </Column>

            <Column field="uuid" header="Id">
                <template #body="slotProps">
                    <span> {{ slotProps.data.reference }} </span>
                </template>
            </Column>

            <Column field="action" header="Action">
                <template #body="{ data }">
                    <div class="d-flex flex-row">
                        <Button
                            size="small"
                            icon="pi pi-eye"
                            class="p-button-eye"
                            style="margin-right: 0.5em"
                            title="Voir"
                            @click="() => {
                              if (type === 'regionBloc') {
                                router.push({ name: 'middleMenuItemList', params: { uuid: data.uuid, type: 'region' } })
                              } else if (type === 'travelBloc') {
                                router.push({ name: 'middleMenuItemList', params: { uuid: data.uuid, type: 'travel' } })
                              } else {
                                router.push({ name: 'middleMenuItemList', params: { uuid: data.uuid } })
                              }
                            }"
                        />

                        <Button
                            size="small"
                            icon="pi pi-pencil"
                            class="p-edition"
                            style="margin-right: 0.5em"
                            title="Éditer"
                            @click="() => {
                              if (type === 'regionBloc') {
                                router.push({ name: 'editMiddleMenu', params: { uuid: data.uuid, type: 'region' } })
                              } else if (type === 'travelBloc') {
                                router.push({ name: 'editMiddleMenu', params: { uuid: data.uuid, type: 'travel' } })
                              } else {
                                router.push({ name: 'editMiddleMenu', params: { uuid: data.uuid } })
                              }
                            }"
                        />

                        <InputSwitch
                            v-model="data.active"
                            :input-id="data.uuid"
                            @input="changeState(data.uuid, data.active)"
                        />
                    </div>
                </template>
            </Column>

            <template #empty>Aucun produit ne correspond à votre recherche</template>
        </DataTable>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import router from '@/router'
import swal from '@/_helpers/swal'

import DataTable from 'primevue/datatable'
import Button from 'primevue/button'
import Column from 'primevue/column'
import InputSwitch from 'primevue/inputswitch'

import { changeStateMiddleMenu, fetchAll, fetchAllActive } from '@/_services/homepage/middleMenu'
import { IMiddleMenu } from '@/models/homepage/IMiddleMenu'

const tableData = ref<IMiddleMenu[]>([])
const rows = ref(10)
const currentPage = ref(0)
const isLoading = ref(true)
const totalRecords = ref(0)
const criteria = ref('')
const page = ref(1)
const modeSortable = ref(false)

const defaultSort = {
    fieldLabel: 'order[createdAt]',
    fieldValue: 'desc'
}

const route = useRoute()

const type = computed(() => route.meta?.type as string | undefined)

const title = computed(() => {
    switch (type.value) {
        case 'travelBloc':
            return 'GÉRER LES VOYAGES'
        case 'regionBloc':
            return 'GÉRER LES RÉGIONS'
        default:
            return 'Gérer les listes des références'
    }
})

watchEffect(() => {
    loadData()
})

async function loadData() {
    isLoading.value = true
    const [loadingDt, totalRecordsDt, tableDataDt] = await fetchAll(
        rows.value,
        page.value,
        criteria.value,
        defaultSort,
        type.value
    )
    totalRecords.value = totalRecordsDt.value
    tableData.value = tableDataDt.value
    isLoading.value = loadingDt.value
}

const changeState = async (uuid: string, isActive: boolean) => {
    const message = isActive
        ? 'Êtes-vous sûr de vouloir activer cette référence ?'
        : 'Êtes-vous sûr de vouloir désactiver cette référence ?'

    const result = await swal.confirm(message)

    if (result.isConfirmed) {
        await changeStateMiddleMenu(uuid, isActive)
        const msg = isActive
            ? "Vous venez d'activer une référence."
            : "Vous venez de désactiver une référence."
        swal.success('Opération réussie !', msg)
        loadData()
    }
}

function onPaginate(e: any) {
    rows.value = e.rows
    currentPage.value = e.page
    page.value = currentPage.value + 1
    loadData()
}

function onSort(e: any) {
    const sort = e.sortField
        ? {
            fieldLabel: `order[${e.sortField}]`,
            fieldValue: e.sortOrder === 1 ? 'asc' : 'desc'
        }
        : defaultSort

    modeSortable.value = !!e.sortField
    loadData()
}
</script>
